// AzureCallback.js
import { useEffect, useContext } from "react";
import { useMsal, useIsAuthenticated } from "@azure/msal-react";
import { InteractionStatus } from "@azure/msal-browser";
import { useNavigate } from "react-router-dom";
import api from "../api/axios";
import toast from "react-hot-toast";
import { AuthContext } from "../context/AuthContext";

export default function AzureCallback() {
  const { instance, accounts, inProgress } = useMsal();
  const { login, setAuthInProgress } = useContext(AuthContext);
  const navigate = useNavigate();
  const isAuthenticated = useIsAuthenticated();

  useEffect(() => {
    const completeLogin = async () => {

      // 1️⃣ Do NOT run until MSAL finishes redirect
      if (inProgress === InteractionStatus.HandleRedirect) {
        setAuthInProgress(true);
        return;
      }

      // 2️⃣ Wait until MSAL sets account properly
      if (!isAuthenticated || accounts.length === 0) {
        setAuthInProgress(true);
        return;
      }

      try {
        setAuthInProgress(true);

        // Get separate tokens for Microsoft Graph and backend API
        const [graphTokenResp, apiTokenResp] = await Promise.all([
          // Token for Microsoft Graph API
          instance.acquireTokenSilent({
            account: accounts[0],
            scopes: ["User.Read"],
          }),
          // Token for backend API
          instance.acquireTokenSilent({
            account: accounts[0],
            scopes: [`api://${process.env.REACT_APP_AZURE_CLIENT_ID}/access_as_user`],
          }),
        ]);

        // Get user profile from Microsoft Graph
        const graphProfile = await fetch("https://graph.microsoft.com/v1.0/me", {
          headers: { Authorization: `Bearer ${graphTokenResp.accessToken}` },
        }).then(r => r.json());

        // Call backend SSO login with the correct API token
        const res = await api.post("/users/sso-login", { graph: graphProfile }, {
          headers: { Authorization: `Bearer ${apiTokenResp.accessToken}` },
        });

        login(res.data.user, apiTokenResp.accessToken);
        navigate("/app", { replace: true });

      } catch (err) {
        console.error("SSO Callback Error:", err);
        setAuthInProgress(false);
        
        // More specific error handling
        if (err.response?.status === 403) {
          toast.error("Access denied. Please check your permissions or contact administrator.");
        } else if (err.response?.status === 401) {
          toast.error("Authentication failed. Please try logging in again.");
        } else {
          toast.error("Authentication failed. Please login again.");
        }
        
        navigate("/login");
      }
    };

    completeLogin();
  }, [inProgress, accounts, isAuthenticated, setAuthInProgress, instance, login, navigate]);

  return (
    <div style={styles.wrapper}>
      <div style={styles.card}>
        <div style={styles.spinner}></div>
        <h2 style={styles.title}>Authenticating...</h2>
        <p style={styles.subtitle}>Please wait while we sign you in securely.</p>
      </div>
    </div>
  );
}

const styles = {
  wrapper: {
    height: "100vh",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    background:
      "radial-gradient(circle at top, #001f3f, #000814, #00040f)",
  },
  card: {
    padding: 30,
    width: 360,
    borderRadius: 16,
    textAlign: "center",
    background: "rgba(3, 18, 40, 0.85)",
    border: "1px solid #2563eb55",
    backdropFilter: "blur(6px)",
  },
  title: { color: "white", fontSize: 20, marginTop: 20 },
  subtitle: { color: "#9ecbff", fontSize: 14, marginTop: 6 },
  spinner: {
    width: 45,
    height: 45,
    border: "5px solid rgba(255,255,255,0.2)",
    borderTopColor: "#3b82f6",
    borderRadius: "50%",
    margin: "0 auto",
    animation: "spin 0.8s linear infinite",
  },
};

// Spinner animation
const sheet = document.styleSheets[0];
sheet.insertRule(`
@keyframes spin {
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
}`, sheet.cssRules.length);
